import { API, HOST_TULUM } from '@/utils/http'
import { getBaseUrl } from '@/utils/url'

// Preview thumbnails for the Mapbox styles (reused from the global picker).
const previews = import.meta.glob('@/assets/img/mapStylesPreview/*.png', {
  eager: true
}) as Record<string, { default: string }>
const preview = (name: string) => previews[`/src/assets/img/mapStylesPreview/${name}`]?.default

export type BasemapKind = 'mapbox' | 'raster' | 'planetscope' | 'blank'

export interface Basemap {
  key: string
  label: string
  theme: 'light' | 'dark' // controls the trigger's border contrast
  preview: string
  kind: BasemapKind
  value?: string // mapbox style url
  tiles?: string[] // raster tile url templates
  requiresPlanetAccess?: boolean
}

export const DEFAULT_BASEMAP_KEY = 'outdoors'

// A landmass tile used as a live preview thumbnail for the open-source rasters.
const previewTile = (tpl: string) => tpl.replace('{z}', '4').replace('{x}', '8').replace('{y}', '5')

export const BASEMAPS: Basemap[] = [
  { key: 'outdoors', label: 'Outdoors', theme: 'dark', kind: 'mapbox', value: 'mapbox://styles/mapbox/outdoors-v12', preview: preview('outdoor.png') },
  { key: 'satellite', label: 'Satellite', theme: 'light', kind: 'mapbox', value: 'mapbox://styles/mapbox/standard-satellite', preview: preview('satellite.png') },
  { key: 'light', label: 'Light', theme: 'dark', kind: 'mapbox', value: 'mapbox://styles/mapbox/light-v11', preview: preview('light.png') },
  { key: 'dark', label: 'Dark', theme: 'light', kind: 'mapbox', value: 'mapbox://styles/mapbox/dark-v11', preview: preview('dark.png') },
  {
    key: 'osm',
    label: 'OpenStreetMap',
    theme: 'dark',
    kind: 'raster',
    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    preview: previewTile('https://tile.openstreetmap.org/{z}/{x}/{y}.png')
  },
  {
    key: 'opentopo',
    label: 'OpenTopoMap',
    theme: 'dark',
    kind: 'raster',
    tiles: [
      'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://b.tile.opentopomap.org/{z}/{x}/{y}.png',
      'https://c.tile.opentopomap.org/{z}/{x}/{y}.png'
    ],
    preview: previewTile('https://a.tile.opentopomap.org/{z}/{x}/{y}.png')
  },
  {
    key: 'carto-light',
    label: 'Carto Light',
    theme: 'dark',
    kind: 'raster',
    tiles: [
      'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      'https://b.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      'https://c.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
      'https://d.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'
    ],
    preview: previewTile('https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png')
  },
  {
    key: 'carto-dark',
    label: 'Carto Dark',
    theme: 'light',
    kind: 'raster',
    tiles: [
      'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      'https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'
    ],
    preview: previewTile('https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png')
  },
  {
    key: 'esri-imagery',
    label: 'ESRI Imagery',
    theme: 'light',
    kind: 'raster',
    // ESRI serves tiles in {z}/{y}/{x} order, not the Mapbox {z}/{x}/{y}.
    tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
    preview: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/4/5/8'
  },
  {
    key: 'planetscope',
    label: 'PlanetScope',
    theme: 'light',
    kind: 'planetscope',
    requiresPlanetAccess: true,
    preview: preview('satellite.png')
  }
]

// Not part of BASEMAPS (it is never a row in the picker): the base the stack
// falls back to when no Mapbox style is enabled, so switching every basemap off
// leaves an empty canvas instead of whichever style happened to be applied last.
export const BLANK_BASEMAP_KEY = 'blank'

export const BLANK_BASEMAP: Basemap = {
  key: BLANK_BASEMAP_KEY,
  label: 'None',
  theme: 'light',
  kind: 'blank',
  preview:
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="76" height="76"><rect width="76" height="76" fill="%23e2e8f0"/></svg>'
    )
}

// Glyphs stay in so symbol layers (feature labels, cluster counts) keep rendering
// over the empty background.
export const blankStyle = () => ({
  version: 8,
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
  sources: {},
  layers: [{ id: 'bg', type: 'background' as const, paint: { 'background-color': '#e2e8f0' } }]
})

const byKey = new Map([...BASEMAPS, BLANK_BASEMAP].map(b => [b.key, b]))

export const getBasemap = (key?: string): Basemap => byKey.get(key || '') || BASEMAPS[0]

interface PlanetMosaic {
  id: string
  name: string
  first_acquired: string
}

export interface PlanetscopeMosaics {
  monthly: PlanetMosaic[]
  quarterly: PlanetMosaic[]
}

// The month/quarter the user (or the stack's default resolution) settled on,
// plus the mosaic name it resolves to. Shared between BasemapStack, the month
// picker and the share-link hash.
export interface PlanetscopeSelection {
  tab: 'monthly' | 'quarterly'
  year?: string
  month?: string
  quarter?: string
  name?: string
}

// Tile template for a specific PlanetScope mosaic (keyed by mosaic name).
// Planet's planet-tiles path keys off the mosaic name, not its id.
export const planetscopeMosaicTiles = (orgId: number, mosaicName: string): string[] =>
  [`${getBaseUrl()}/tulum/tiles/planetscope/${orgId}/${mosaicName}/{z}/{x}/{y}.png`]

// Every basemap consumer of the mosaic list wants it newest-first, while the
// endpoint returns oldest-first. Cached per org so several tiles plus the month
// picker share one request; the entry is evicted on failure so a transient error
// isn't cached for the session.
const mosaicCache = new Map<number, Promise<PlanetscopeMosaics>>()

export const fetchPlanetscopeMosaics = (orgId: number): Promise<PlanetscopeMosaics> => {
  const cached = mosaicCache.get(orgId)
  if (cached) return cached
  const request = API({ method: 'GET', url: `${HOST_TULUM}/tiles/planetscope/${orgId}` })
    .then(({ data }: any) => ({
      monthly: [...(data?.monthly ?? [])].reverse() as PlanetMosaic[],
      quarterly: [...(data?.quarterly ?? [])].reverse() as PlanetMosaic[]
    }))
    .catch((error: any) => {
      mosaicCache.delete(orgId)
      console.error('Failed to load PlanetScope mosaics:', error)
      return { monthly: [], quarterly: [] }
    })
  mosaicCache.set(orgId, request)
  return request
}

// Shaped like the month picker's own selection so its restoreSelection() lands
// on whatever the stack already drew instead of resetting to the newest month.
export const latestPlanetscopeSelection = async (orgId: number): Promise<PlanetscopeSelection | null> => {
  const { monthly } = await fetchPlanetscopeMosaics(orgId)
  const latest = monthly[0]
  if (!latest) return null
  const date = new Date(latest.first_acquired)
  return {
    tab: 'monthly',
    year: String(date.getUTCFullYear()),
    month: String(date.getUTCMonth() + 1),
    name: latest.name
  }
}

// Apply a basemap to a Mapbox GL instance. Overlay layers re-add themselves on
// the map's `style.load` event. Only Mapbox styles and the blank fallback reach
// here — BasemapStack owns every raster / PlanetScope basemap and renders it as
// a `bm-*` layer over this style.
// `diff: false`: a successful style diff swaps the style in place, dropping every
// source/layer we added (draw included) WITHOUT firing `style.load`, so nothing
// re-adds them and drawing stays dead until reload.
export const applyBasemap = (mapInstance: any, key: string) => {
  if (!mapInstance) return
  const bm = getBasemap(key)
  mapInstance.setStyle(bm.kind === 'mapbox' && bm.value ? bm.value : blankStyle(), { diff: false })
}

// --- Mapbox view toggles (labels / 3D buildings / 3D terrain) -------------------
// Imperative helpers shared by BasemapStack (on user toggle) and MapCanvas (re-apply
// on every style.load, since setStyle wipes added layers/sources). Only meaningful
// for Mapbox vector styles.
const BUILDING_LAYER_ID = 'add-3d-buildings'

export const applyLabels = (mapInstance: any, on: boolean, isSatellite: boolean) => {
  if (!mapInstance) return
  if (isSatellite) {
    mapInstance.setConfig('basemap', {
      showPlaceLabels: on,
      showRoadsAndTransit: on,
      showRoadLabels: on,
      showPointOfInterestLabels: on,
      showTransitLabels: on,
    })
    return
  }
  mapInstance.style.stylesheet.layers.forEach((layer: any) => {
    if (layer.type === 'symbol' || layer.type === 'line') {
      mapInstance.setLayoutProperty(layer.id, 'visibility', on ? 'visible' : 'none')
    }
  })
}

export const applyBuildings = (mapInstance: any, on: boolean) => {
  if (!mapInstance) return
  const exists = !!mapInstance.getLayer(BUILDING_LAYER_ID)
  if (!on) {
    if (exists) mapInstance.removeLayer(BUILDING_LAYER_ID)
    return
  }
  if (exists) return
  if (!mapInstance.getSource('composite')) return
  const labelLayer = mapInstance.getStyle().layers.find(
    (layer: any) => layer.type === 'symbol' && layer.layout?.['text-field']
  )
  if (!labelLayer) return

  mapInstance.addLayer(
    {
      id: BUILDING_LAYER_ID,
      source: 'composite',
      'source-layer': 'building',
      filter: ['==', 'extrude', 'true'],
      type: 'fill-extrusion',
      minzoom: 15,
      paint: {
        'fill-extrusion-color': '#aaa',
        'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
        'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'min_height']],
        'fill-extrusion-opacity': 0.6,
      },
    },
    labelLayer.id
  )
}

export const applyTerrain = (mapInstance: any, on: boolean) => {
  if (!mapInstance) return
  const exists = !!mapInstance.getSource('mapbox-dem')
  if (on) {
    if (exists) return
    mapInstance.addSource('mapbox-dem', {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    })
    mapInstance.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 })
  } else if (exists) {
    mapInstance.setTerrain(null)
    mapInstance.removeSource('mapbox-dem')
  }
}

export interface MapViewSettings {
  labels?: boolean
  buildings?: boolean
  terrain?: boolean
}

// Re-apply the saved view toggles to a freshly-loaded Mapbox style. No-op when
// there's nothing saved or the basemap isn't a Mapbox vector style.
export const applyMapViewToggles = (mapInstance: any, key: string, settings?: MapViewSettings | null) => {
  if (!mapInstance || !settings) return
  const bm = getBasemap(key)
  if (bm.kind !== 'mapbox') return
  applyLabels(mapInstance, settings.labels !== false, bm.label === 'Satellite')
  applyBuildings(mapInstance, !!settings.buildings)
  applyTerrain(mapInstance, !!settings.terrain)
}
